import { AuthRole } from '@newfront-insurance/auth-api';
import type { Provider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';

import type { AuthProviderProps } from '../providers/auth-provider';
import { hasAnyScope, hasAllScopes, hasScope } from '../scopes';
import type { UsePermissionOptions, AnyAuthProviderContext } from '../types';

export function useHasAnyScope<T extends AnyAuthProviderContext, P = AuthProviderProps>(
  authProvider: Provider<T, P>,
  scopes: string[],
): boolean {
  const { userDetails: user } = useProvider(authProvider);

  if (!user) {
    return false;
  }

  return hasAnyScope(user.scopes ?? [], scopes);
}

export function useHasAllScopes<T extends AnyAuthProviderContext, P = AuthProviderProps>(
  authProvider: Provider<T, P>,
  scopes: string[],
): boolean {
  const { userDetails: user } = useProvider(authProvider);

  if (!user) {
    return false;
  }

  return hasAllScopes(user.scopes ?? [], scopes);
}

export function useHasScope<T extends AnyAuthProviderContext, P = AuthProviderProps>(
  authProvider: Provider<T, P>,
  scope: string,
): boolean {
  const { userDetails: user } = useProvider(authProvider);

  if (!user) {
    return false;
  }

  return hasScope(user.scopes ?? [], scope);
}

export function usePermission<T extends AnyAuthProviderContext, P = AuthProviderProps>(
  authProvider: Provider<T, P>,
  opts: UsePermissionOptions,
): boolean {
  const { scopes, hasAll } = opts;
  const hasAnyScopeFn = useHasAnyScope(authProvider, scopes);
  const hasAllScopesFn = useHasAllScopes(authProvider, scopes);

  if (hasAll) {
    return hasAllScopesFn;
  }

  return hasAnyScopeFn;
}

export function useIsAccountManager<T extends AnyAuthProviderContext, P = AuthProviderProps>(
  authProvider: Provider<T, P>,
): boolean {
  return useHasScope(authProvider, AuthRole.ACCOUNT_MANAGER);
}

export function useIsProducer<T extends AnyAuthProviderContext, P = AuthProviderProps>(
  authProvider: Provider<T, P>,
): boolean {
  return useHasScope(authProvider, AuthRole.PRODUCER);
}

export function useGetUserScopes<T extends AnyAuthProviderContext, P = AuthProviderProps>(
  authProvider: Provider<T, P>,
): string[] {
  const { userDetails: user } = useProvider(authProvider);

  if (!user) {
    return [];
  }

  return user.scopes ?? [];
}
